import {
  AssetClassModelInputs,
  useCmaAssetClassBoundariesRetrieveQuery,
} from "@app/api/carApi.generated";
import {
  ExcelColumn,
  ExcelDataFormat,
  ExcelTable,
  useExcelExport,
} from "@app/hooks/useExcelExport";
import { useCallback, useMemo } from "react";
import { formatDate } from "@app/utils";
import { pendoClasses } from "@app/core/thirdParty/pendo";
import {
  AssetClass,
  AssetClassLoadType,
  useAssetClasses,
} from "@app/features/assets/useAssetClasses";
import { useAssetClassSelector } from "@app/hooks/useAssetClassSelector";

export interface BoundaryItem {
  year: number;
  boundaryType: string;
  expectedReturn?: number;
  residualLowerBoundary?: number;
  lowerBoundaryRate?: number;
  residualUpperBoundary?: number;
  upperBoundaryRate?: number;
}

export interface GrowthDataItem {
  year: number;
  lowerDollarGrowth?: number;
  lowerCompoundReturn?: number;
  upperDollarGrowth?: number;
  upperCompoundReturn?: number;
}

export const useSimulationModelsBoundaries = () => {
  const excelExport = useExcelExport();

  const acFilter = useCallback(
    (ac: AssetClass) => (ac.isEquity && !ac.isRealEstate) || ac.isGold,
    [],
  );

  const assetClasses = useAssetClasses({
    type: AssetClassLoadType.allFirm,
    filter: acFilter,
  });

  const assetClassSelector = useAssetClassSelector({
    assetClasses,
  });

  const boundaries = useCmaAssetClassBoundariesRetrieveQuery(
    {
      assetClassId: assetClassSelector.value ?? "",
    },
    {
      skip: !assetClassSelector.value,
    },
  );

  const boundariesData = boundaries.data;

  return useMemo(() => {
    const getExcelTable = (data: AssetClassModelInputs): ExcelTable => {
      const traces = data.traces;

      const rowKeys = Array.from(
        new Set(traces.flatMap((i) => Object.keys(i.data))),
      ).sort((a, b) => a.localeCompare(b));
      return {
        name: data.name,
        columns: [
          {
            label: "",
            format: ExcelDataFormat.general,
            fraction: 1,
            charWidth: 14,
          },
          ...traces.map<ExcelColumn>((i) => {
            return {
              label: i.name,
              pendoClassName: pendoClasses.simulationModelsBoundaries(i.name),
              format:
                typeof Object.values(i.data).at(0) === "boolean"
                  ? ExcelDataFormat.boolean
                  : ExcelDataFormat.float,
              decimalOptions: {
                decimalPlaces: 6,
                forceShowDecimals: true,
              },
            };
          }),
        ],

        rows: rowKeys.map((key) => [
          formatDate(key),
          ...traces.map((c) => c.data[key] ?? null),
        ]),
      };
    };

    const boundaryGroup =
      boundariesData?.model_input_data?.["boundary_group"] ?? [];

    const tables = boundaryGroup.map((i) => getExcelTable(i));

    const handleDownloadReports = () => {
      excelExport.exportAllExcel({
        fileName: `Simulation Models - ${assetClassSelector.selectedItem?.name}`,
        tables,
      });
    };

    const boundaryItems =
      boundariesData?.boundary_data.boundaries
        .map<BoundaryItem>((i) => ({
          year: i.year,
          boundaryType: i.type ?? "-",
          expectedReturn: i.expected_return ?? undefined,
          residualLowerBoundary: i.lower_residual ?? undefined,
          lowerBoundaryRate: i.lower_boundary ?? undefined,
          residualUpperBoundary: i.upper_residual ?? undefined,
          upperBoundaryRate: i.upper_boundary ?? undefined,
        }))
        .sort((a, b) => a.year - b.year) ?? [];

    const growthDataItems =
      boundariesData?.boundary_data.growth_data
        ?.map<GrowthDataItem>((i) => ({
          year: i.boundary_year,
          lowerDollarGrowth: i.lower_growth ?? undefined,
          lowerCompoundReturn: i.lower_multiplier ?? undefined,
          upperDollarGrowth: i.upper_growth ?? undefined,
          upperCompoundReturn: i.upper_multiplier ?? undefined,
        }))
        .sort((a, b) => a.year - b.year) ?? [];

    const forwardReturnsItems =
      boundariesData?.boundary_data.forward_returns_data ?? [];

    return {
      isLoading: assetClassSelector.isLoading || boundaries.isLoading,
      assetClassSelector,
      boundaryItems,
      growthDataItems,
      forwardReturnsItems,
      tables,
      notes: boundariesData?.notes,
      handleDownloadReports,
    };
  }, [boundariesData, assetClassSelector, boundaries.isLoading, excelExport]);
};
